import { MatchInfo, SearchOptions, SearchResult } from "minisearch";
import { searchIndex } from "../../../utils/searchIndex.js";
import { Page } from "../../../utils/pageInterface.js";
import { ReferenceSections } from "../../ApiReference/referenceSectionsEnum";
import { miniSearch } from "../SearchModal";
import { removeHtmlAndSpecificTags } from "../../../utils/searchIndexUtils";
import { ApiMethodParameter } from "../../ApiReference/ApiMethod";
import { ApiObjectProperty } from "../../ApiReference/ApiObject";

const EXCERPT_CHAR_LENGTH = 200;

interface ReferenceItem {
    description: string;
    parameters?: ApiMethodParameter[];
    returns?: string;
    example?: string;
    isBeta?: boolean;
    properties?: ApiObjectProperty[];
    remarks?: string;
}

const findStartingStringIndex = (numCharsAhead: number, numCharsAfter: number, remainingLength: number, foundIndex: number): number => {
    let half: number = Math.floor(remainingLength / 2);
    if (numCharsAhead < half) {
        return 0;
    } else if (numCharsAfter < half) {
        return foundIndex - remainingLength + numCharsAfter;
    } else {
        return foundIndex - half;
    }
};

export const pullText = (searchId: number, matchTerm: string): string => {
    let i: number = searchIndex.findIndex((page: Page) => page.id === searchId);
    let contentString: string = searchIndex[i].content;

    let stringIndex: number = contentString.indexOf(matchTerm);
    if (stringIndex >= 0) {
        let excerpt: string = "";
        let numRemainingChars: number = EXCERPT_CHAR_LENGTH - matchTerm.length;
        let numCharsAhead: number = contentString.substring(0, stringIndex).length;
        let numCharsAfter: number = contentString.substring(stringIndex + matchTerm.length).length;
        if (numCharsAhead + numCharsAfter <= numRemainingChars) {
            return contentString;
        }
        let excerptStartingIndex: number = findStartingStringIndex(numCharsAhead, numCharsAfter, numRemainingChars, stringIndex);
        excerpt += contentString.substring(excerptStartingIndex, stringIndex);
        numRemainingChars -= excerpt.length;
        if (excerptStartingIndex > 0) {
            excerpt = "..." + excerpt;
        }
        excerpt += matchTerm;
        excerpt += contentString.substring(stringIndex + matchTerm.length, stringIndex + matchTerm.length + numRemainingChars);
        if (excerpt.length - excerpt.indexOf(matchTerm) - matchTerm.length !== numCharsAfter) {
            excerpt += "...";
        }
        return excerpt;
    }
    //if there's no match in the page content, return the first portion of the page
    return contentString.substring(0, EXCERPT_CHAR_LENGTH) + "...";
};

export const findHeaderId = (searchId: number, matchTerms: MatchInfo): string | null => {
    let isHeaderLink = false;
    let headerMatch = "";
    for (const term in matchTerms) {
        if (matchTerms[term].includes("headers")) {
            isHeaderLink = true;
            headerMatch = term;
            break;
        }
    }
    if (isHeaderLink) {
        let i: number = searchIndex.findIndex((page: Page) => page.id === searchId);
        let pageHeaders: string[] = searchIndex[i].headers;
        let headerIndex = pageHeaders.findIndex((header) => header.toLowerCase().includes(headerMatch));
        return searchIndex[i].headerIds[headerIndex];
    }
    return null;
};

export const searchAndFilter = (query: string): SearchResult[] => {
    const strictSearch: SearchOptions = {
        fields: ["title", "content", "headers"],
        boost: {
            title: 3,
            headers: 2
        },
        fuzzy: 0,
        prefix: false
    };
    const looseyGoosey: SearchOptions = {
        fields: ["title", "content", "headers"], // fields to index for full-text search
        fuzzy: 0.2,
        prefix: true,
        boost: {
            title: 3,
            headers: 2
        }
    };
    let results: SearchResult[] = [];

    if (query.indexOf(" ") >= 0) {
        //there's no exact match in the minisearch library but this is as close as we can get
        let filteredResults: SearchResult[] = [];
        results = miniSearch.search(query, strictSearch);
        let queryTerms: string[] = query.split(" ");
        results.forEach((page: SearchResult) => {
            let isSubset = !queryTerms.some((string) => page.terms.indexOf(string) === -1);
            if (isSubset) {
                filteredResults.push(page);
            }
        });
        return filteredResults;
    } else {
        results = miniSearch.search(query, looseyGoosey);
    }
    return results;
};

export const addParsedResultsToSearchIndex = (json: { [key: string]: ReferenceItem }, referenceSection: ReferenceSections) => {
    Object.keys(json).forEach((key) => {
        let entry = json[key];
        let itemType: string = "parameters" in entry ? "Methods" : "Objects";
        let linkBase: string = referenceSection === ReferenceSections.MyGeotab ? "/myGeotab" : "/myAdmin";
        let link: string = `${linkBase}/apiReference/${itemType.toLowerCase()}/${key}`;
        if (searchIndex.findIndex((document) => document.link === link) < 0) {
            let searchDocument: Page = { id: 0, title: "", headers: [], headerIds: [], content: "", link: "", breadCrumb: [], category: "" };
            let currentId: number = searchIndex[searchIndex.length - 1].id;
            searchDocument.id = currentId + 1;
            searchDocument.title = key;
            searchDocument.headers = ["Introduction", "Parameters", "Return value", "Code samples"];
            searchDocument.headerIds = ["introduction", "parameters", "return value", "code samples"];
            searchDocument.link = link;
            searchDocument.breadCrumb = [referenceSection, "API Reference", itemType, key];
            searchDocument.content = removeHtmlAndSpecificTags(entry.description);
            if (itemType === "Methods") {
                searchDocument.breadCrumb[searchDocument.breadCrumb.length - 1] += " (...)";
            }
            searchDocument.category = "reference";
            if (entry.parameters) {
                searchDocument.content += removeHtmlAndSpecificTags(JSON.stringify(entry.parameters));
            }
            if (entry.properties) {
                searchDocument.content += removeHtmlAndSpecificTags(JSON.stringify(entry.properties));
            }
            if (entry.returns) {
                searchDocument.content += removeHtmlAndSpecificTags(entry.returns);
            }
            if (entry.remarks) {
                searchDocument.content += removeHtmlAndSpecificTags(entry.remarks);
            }

            searchIndex.push(searchDocument);
            miniSearch.add(searchDocument);
        }
    });
};
